import { useState } from 'react';

import { cn } from '@/lib/utils';

import { QuietFrame } from '../internals/QuietFrame';
import { useScreenshotImage } from '../internals/useScreenshotImage';
import { sizingClass, type Sizing } from '../tokens';

import type { catalog } from '@littlebird/action-cards-view';
import type { ComponentFn } from '@littlebird/action-cards-view/react';

// A phone screen's shape holds the space until the image reports its own
const PHONE_ASPECT_RATIO = 9 / 19.5;

// The height is fixed and the width follows the image, so nothing below the
// part moves when it loads. `no-record` and `ph-no-capture` keep the
// screenshot out of session recordings
const frameClass = (sizing: Sizing | undefined) =>
  cn(
    'no-record ph-no-capture relative h-[calc(224*var(--ac-u))] max-w-full self-start overflow-hidden rounded-lg border border-border bg-gray-2 dark:bg-gray-3',
    sizingClass(sizing) || 'shrink-0'
  );

export const ScreenshotImage: ComponentFn<
  typeof catalog,
  'ScreenshotImage'
> = ({ props }) => {
  const alt = props.alt || 'Screenshot';
  const { state, opening, openFullView } = useScreenshotImage(
    props.previewId,
    alt
  );
  const [aspectRatio, setAspectRatio] = useState(PHONE_ASPECT_RATIO);
  const [painted, setPainted] = useState(false);
  const frame = frameClass(props.sizing);

  switch (state.status) {
    case 'loading':
      return (
        <div
          role="img"
          aria-label={alt}
          aria-busy
          className={frame}
          style={{ aspectRatio }}
        />
      );
    case 'unavailable':
      return (
        <QuietFrame className={frame} aspectRatio={aspectRatio}>
          Screenshot no longer available
        </QuietFrame>
      );
    case 'failed':
      return (
        <QuietFrame className={frame} aspectRatio={aspectRatio}>
          Screenshot couldn&apos;t load
        </QuietFrame>
      );
    case 'shown':
      return (
        <button
          type="button"
          aria-haspopup="dialog"
          aria-busy={opening}
          onClick={() => void openFullView()}
          className={cn(
            frame,
            'focus-ring p-0',
            opening ? 'cursor-progress' : 'cursor-zoom-in'
          )}
          style={{ aspectRatio }}
        >
          <img
            src={state.src}
            alt={alt}
            draggable={false}
            onLoad={(event) => {
              const { naturalWidth, naturalHeight } = event.currentTarget;
              if (naturalWidth > 0 && naturalHeight > 0) {
                setAspectRatio(naturalWidth / naturalHeight);
              }
              setPainted(true);
            }}
            className={cn(
              'size-full object-contain transition-opacity duration-150 ease-out motion-reduce:transition-none',
              painted ? 'opacity-100' : 'opacity-0'
            )}
          />
        </button>
      );
  }
};
